// hooks/designer-application-context/utils.ts

import {
  DesignerApplicationContextAction,
  DesignerApplicationContextActions,
  DesignerApplicationContextState,
} from "./types";

/** Initial context state with safe no-op implementations */
export const DESIGNER_APPLICATION_INITIAL_STATE: DesignerApplicationContextState = {
  application: null,
  loading: false,
  refreshApplicationData: () => {},
  updateApplicationData: () => {},
  createApplication: async () => undefined,
  saveDesignerApplication: async () => undefined,
  approveDesignerApplication: async () => true,
  rejectDesignerApplication: async () => true,
  hasApplicationDataLoaded: false,
};

/** Reducer: supports both full-state merges and application-only patches */
export function designerApplicationReducer(
  state: DesignerApplicationContextState,
  action: DesignerApplicationContextAction
): DesignerApplicationContextState {
  switch (action.type) {
    case DesignerApplicationContextActions.SET_APPLICATION: {
      return { ...state, application: action.payload };
    }
    case DesignerApplicationContextActions.UPDATE_APPLICATION: {
      const payload = action.payload as any;

      // If payload targets top-level fields (e.g., { application: null }), shallow-merge into state
      if (
        payload &&
        (Object.prototype.hasOwnProperty.call(payload, "application") ||
          Object.prototype.hasOwnProperty.call(payload, "loading") ||
          Object.prototype.hasOwnProperty.call(payload, "hasApplicationDataLoaded"))
      ) {
        return { ...state, ...payload };
      }

      // Otherwise treat payload as an application patch
      return {
        ...state,
        application: {
          ...(state.application ?? {}),
          ...(payload ?? {}),
        },
      };
    }
    default:
      return state;
  }
}

/** Recursively remove GraphQL __typename (and similar) keys from an object/array */
export function removeTypeNames<T = any>(value: T, _designerId?: string): T {
  if (Array.isArray(value)) {
    return value.map((v) => removeTypeNames(v, _designerId)) as unknown as T;
  }
  if (value && typeof value === "object") {
    const out: Record<string, any> = {};
    for (const [k, v] of Object.entries(value as Record<string, any>)) {
      if (k === "__typename") continue;
      out[k] = removeTypeNames(v, _designerId);
    }
    return out as T;
  }
  return value;
}
